export class AssetsConfig {
  static S3_CONFIG_BASE = "https://gni-public-bucket.s3.amazonaws.com/assets";
  
  static getBusinessIdFromSubdomain() {
    const hostname = window.location.hostname;
    if (hostname.startsWith('oscdev') || hostname.startsWith('osc')){
      return 'osc';
    } 
    if (hostname.startsWith('gratitwood-dev')){
      return 'gratitwood';
    }
    if (hostname.startsWith('rmh-dev')|| hostname.startsWith('rmh')){
      return 'rmh';
    }
    if (hostname.startsWith('millcitycabinetry-dev')){
      return 'millcitycabinetry';
    }
    if (hostname.startsWith('test-dev')){
      return 'test';
    }
    else {
      return 'gni';
    }
  }

  static getBusinesdomain(BusinessId) {
    if (BusinessId === 'gni') {
      return 'https://www.gnibox.com/';
    }
    if (BusinessId === 'osc'){
      return 'https://onesourcecabinets.com/';
    }
    if (BusinessId === 'gratitwood'){
      return 'https://www.gratitwood.com/';
    }
    if (BusinessId === 'rmh'){
      return 'https://www.rmhkitchenandbath.com/';
    }
    if (BusinessId === 'millcitycabinetry'){
      return 'https://www.millcitycabinetry.com/';
    }
    return 'https://www.gnibox.com/';
  }
  
  static async getBusinessConfig(businessId = null) {
    try {
      // If no businessId provided, get it from subdomain
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      
      // Import the appropriate config file based on business ID
      let config;
      try {
        // Dynamic import of business-specific config
        const module = await import(`@/assets/${currentBusinessId}/inspiration.json`);
        config = module.default;
      } catch {
        // If business-specific config fails to load, use default
        const defaultModule = await import('@/assets/gni/inspiration.json');
        config = defaultModule.default;
      }

      // this.configCache.set(currentBusinessId, config);
      return config;
    } catch (error) {
      console.error('Error loading business configuration:', error);
      throw error;
    }
  }

  static async getMaterials(businessId = null, projectType = "kitchen") {
    try {
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      const uniqueParam = `?cacheBuster=${new Date().getTime()}`;
      const materialsUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/moodboardConfig.json${uniqueParam}`;
      
      const response = await fetch(materialsUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      let materials;
      if (data?.[projectType]?.materials) {
        materials = data[projectType].materials;
      } else if (data?.default?.materials) {
        materials = data.default.materials;
      } else {
        materials = [];
      }
      
      return {
        materials,
        businessId: currentBusinessId
      };
    } catch (error) {
      console.error('Error loading business materials:', error);
      if (businessId !== 'gni') {
        console.log('Falling back to default GNI materials');
        return this.getMaterials('gni', projectType);
      }
      throw error;
    }
  }

  static async getTypeLegacy (businessId = null) {
    try {
        const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();

        const materialsUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/moodboardConfig.json`;

        const response = await fetch(materialsUrl);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        return data.materialTypeMapping || {};

    } catch (error) {
        console.error('Error loading business materials:', error);
        if (businessId !== 'gni') {
            console.log('Falling back to default GNI materials');
            return this.getTypeLegacy('gni');
        }
        throw error;
    }
  } 

  static getImageUrl(item, businessId = null) {
    const fallbackImage = 'https://gni-public-bucket.s3.us-east-1.amazonaws.com/assets/gni/notAvailible.jpg';

    if (!item || item.isUnavailable || item.FileName === 'UNAVAILABLE') {
        return fallbackImage;
    }

    // Regular material handling with fallback for errors
    try {
        const businessId = this.getBusinessIdFromSubdomain();
        return `${this.S3_CONFIG_BASE}/${businessId}/materials/${item.Type}/${item.FileName}`.replaceAll(' ', '+');
    } catch (error) {
        console.error('Error resolving image URL:', error);
        return fallbackImage;
    }
  }

  static getApplianceIconUrl = (iconPath, businessId = null) => {
    const currentBusinessId = businessId || AssetsConfig.getBusinessIdFromSubdomain();
    return `${AssetsConfig.S3_CONFIG_BASE}/${currentBusinessId}/${iconPath}`;
  };

  static getLogoUrl(main=true) {
    const businessId = this.getBusinessIdFromSubdomain();
    const logo_name = `${businessId}_main_logo.png`
    const buisnessDomain = this.getBusinesdomain(businessId)

    if (!main) {
      logo_name = `${businessId}_logo.png`
    };

    let path;
    try {
      // Dynamic import of business-specific config
      path = `${this.S3_CONFIG_BASE}/${businessId}/${logo_name}`.replaceAll(' ', '+');
    } catch {
      // If business-specific config fails to load, use default
      path = `${this.S3_CONFIG_BASE}/gni/${logo_name}`.replaceAll(' ', '+');
    }
    return {'logo_url': path, "link": buisnessDomain};
  }

  static async getAppliances(businessId = null, projectType = "kitchen") {
    try {
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      const uniqueParam = `?cacheBuster=${new Date().getTime()}`;
      const appliancesUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/appliancesConfig.json${uniqueParam}`;
      
      const response = await fetch(appliancesUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      let projectAppliances = [];
      // Combine project-specific appliances with default ones
      if (data[projectType] === undefined) {
        projectAppliances = data["default"].appliances;
        projectType = "default";
      } else {
        projectAppliances = data[projectType]?.appliances || [];
      }

      // Add the icon URL to each appliance
      const appliancesWithIcons = projectAppliances.map(appliance => ({
        ...appliance,
        iconUrl: `${this.S3_CONFIG_BASE}/${currentBusinessId}/icons/${appliance.icon}`
      }));

      const result = {
        appliances: appliancesWithIcons,
        standardSizes: data[projectType]?.standardSizes || {},
        applianceTypes: data[projectType]?.applianceTypes || {}
      };
      return result;
    } catch (error) {
      console.error('Error loading business appliances:', error);
      if (businessId !== 'gni') {
        console.log('Falling back to default GNI appliances');
        return this.getAppliances('gni', projectType);
      }
      throw error;
    }
  }

  static async getCabinets(businessId = null, projectType = "kitchen") {
    try {
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      const uniqueParam = `?cacheBuster=${new Date().getTime()}`;
      const cabinetsUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/cabinetsConfig.json${uniqueParam}`;
      
      const response = await fetch(cabinetsUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      let projectCabinets = [];
      // Combine project-specific appliances with default ones
      if (data[projectType] === undefined) {
        projectCabinets = data["default"].cabinets;
        projectType = "default";
      } else {
        projectCabinets = data[projectType]?.cabinets || [];
      }

      // Add the icon URL to each appliance
      const appliancesWithIcons = projectCabinets.map(cabinet => ({
        ...cabinet,
        iconUrl: `${this.S3_CONFIG_BASE}/${currentBusinessId}/icons/${cabinet.icon}`
      }));

      const result = {
        cabinets: appliancesWithIcons,
        standardSizes: data[projectType]?.standardSizes || {},
        cabinetsTypes: data[projectType]?.cabinetsTypes || {}
      };
      return result;
    } catch (error) {
      console.error('Error loading business cabinets:', error);
      if (businessId !== 'gni') {
        console.log('Falling back to default GNI cabinets');
        return this.getCabinets('gni', projectType);
      }
      throw error;
    }
  }

  static getFaviconUrl() {
    const businessId = this.getBusinessIdFromSubdomain();
    const faviconName = `${businessId}_logo_favicon.ico`;
    
    let path;
    try {
      path = `${this.S3_CONFIG_BASE}/${businessId}/${faviconName}`.replaceAll(' ', '+');
    } catch {
      // If business-specific favicon fails to load, use default
      path = `${this.S3_CONFIG_BASE}/gni/${faviconName}`.replaceAll(' ', '+');
    }
    return path;
  }

  static async getQuestions({ businessId = null, flow = "default" } = {}) {
    try {
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      const uniqueParam = `?cacheBuster=${new Date().getTime()}`;
      const questionsUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/AdditionalQuestions.json${uniqueParam}`;
      
      try {
        const response = await fetch(questionsUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const config = await response.json();
        if (config[flow] === undefined) {
          return config["default"];
        } else {
          return config[flow];
        }
      } catch (error) {
        if (currentBusinessId !== 'gni') {
          console.log('Falling back to default GNI questions');
          return this.getQuestions({ businessId: 'gni', flow: flow });
        }
        throw error;
      }
    } catch (error) {
      console.error('Error loading questions configuration:', error);
      throw error;
    }
  }

  static async getConfigJSON({ fileName = "startProjectConfig", businessId = null } = {}) {
   
    try {
      const currentBusinessId = businessId || this.getBusinessIdFromSubdomain();
      const uniqueParam = `?cacheBuster=${new Date().getTime()}`;
      const questionsUrl = `${this.S3_CONFIG_BASE}/${currentBusinessId}/${fileName}.json${uniqueParam}`;
      
      try {
        const response = await fetch(questionsUrl);
        if (!response.ok) {
          if (currentBusinessId !== 'gni') {
            return await this.getConfigJSON({ fileName: fileName, businessId: 'gni' });
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const config = await response.json();
        console.log("config file in asset config:", config);
        return config;
      } catch (error) {
        if (currentBusinessId !== 'gni') {
          return await this.getConfigJSON({ fileName: fileName, businessId: 'gni' });
        }
        throw error;
      }
    } catch (error) {
      console.error('Error loading questions configuration:', error);
      throw error;
    }
  }
}


